import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import PremiumLockComponent from '../components/PremiumLockComponent';
import { API_BASE_URL } from '../config';
import RecipeCard from '../components/RecipeCard';
import RecipeModal from '../components/RecipeModal';
import { hydrateRecipeForModal } from '../utils/recipeModalData';
import RecipeMenuGroup from '../components/RecipeMenuGroup';
import AllergyReviewModal from '../components/AllergyReviewModal';
import Swal from 'sweetalert2';
import useMenuApproval from '../hooks/useMenuApproval';

const AllergenKitchen = ({ isDarkMode, toggleDarkMode }) => {
    const searchSeqRef = useRef(0);
    const isRecipeAdapted = (recipe) => !!(
        recipe?.isAdapted ||
        recipe?.match?.isAdapted ||
        recipe?.facts?.isAdapted ||
        recipe?.presentation?.isAdapted
    );
    const [loading, setLoading] = useState(true);
    const [booting, setBooting] = useState(true);
    const [hasPremium, setHasPremium] = useState(false);
    const [recipes, setRecipes] = useState([]);
    const [structuredMenu, setStructuredMenu] = useState(null);
    const [browseRecipes, setBrowseRecipes] = useState([]);
    const [availableComponents, setAvailableComponents] = useState([]);
    const [userProfile, setUserProfile] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState(['main', 'soup', 'salad']);
    const [selectedAllergens, setSelectedAllergens] = useState([]);
    const [viewMode, setViewMode] = useState('matching'); // 'matching' | 'all'
    const [portion, setPortion] = useState('4');
    const [skillLevel, setSkillLevel] = useState(() => localStorage.getItem('smartSearch_skillLevel') || 'Acemi');
    const [selectedRecipeNames, setSelectedRecipeNames] = useState([]);
    const [includeAdaptiveRecipes, setIncludeAdaptiveRecipes] = useState(false);
    const [showAdaptivePrompt, setShowAdaptivePrompt] = useState(false);
    const [pulseAdaptivePrompt, setPulseAdaptivePrompt] = useState(false);
    const [showOnlyAdaptiveRecipes, setShowOnlyAdaptiveRecipes] = useState(false);
    // [Sayfalı browse] "Tüm Tarifler" 20'şer yüklenir; "Daha fazla" ile devamı.
    const [browseHasMore, setBrowseHasMore] = useState(false);
    const [browsePage, setBrowsePage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const BROWSE_PAGE_SIZE = 20;

    const { approveMenu, isApproving } = useMenuApproval({
        onSuccess: () => {
            setSelectedRecipeNames([]);
            handleSearch(selectedCategories, selectedAllergens, viewMode);
        }
    });

    // Recipe Modal State
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [recipeModalOpen, setRecipeModalOpen] = useState(false);

    // Allergen Review Modal State
    const [reviewModalOpen, setReviewModalOpen] = useState(false);

    const categories = [
        { id: 'soup', label: 'Çorba', icon: 'soup_kitchen' },
        { id: 'main', label: 'Ana Yemek', icon: 'restaurant' },
        { id: 'salad', label: 'Salata/Meze', icon: 'eco' },
        { id: 'dessert', label: 'Tatlı', icon: 'cake' }
    ];

    const commonAllergens = [
        'Yok', 'Gluten', 'Süt', 'Yumurta', 'Fındık', 'Fıstık', 'Balık', 'Soya', 'Susam'
    ];

    const hasActiveAllergenFilter = selectedAllergens.some((item) => !String(item || '').toLowerCase().includes('yok'));
    const hasRenderableResults = viewMode === 'all'
        ? browseRecipes.length > 0
        : (!!structuredMenu || recipes.length > 0);

    useEffect(() => {
        const initialize = async () => {
            setBooting(true);
            setLoading(true);
            try {
                const token = localStorage.getItem('authToken');
                const [preloadRes, planningRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/allergen-kitchen/preload`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${API_BASE_URL}/api/planning/config`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).catch(() => ({ data: null }))
                ]);
                const data = preloadRes.data || {};
                const planningConfig = planningRes?.data?.config || null;

                setHasPremium(!!data.hasPremium);
                setUserProfile(data.profile || null);

                const profileAllergens = data.defaults?.selectedAllergens || [];
                if (profileAllergens.length > 0) {
                    setSelectedAllergens(profileAllergens);
                }

                const plannerPortion = planningConfig?.breakfast?.servings;
                const preloadPortion = data.defaults?.portion;
                const finalPortion = String(plannerPortion || preloadPortion || '4');
                setPortion(finalPortion);

                if (data.hasPremium) {
                    if (!data.allergenReviewDone) {
                        setReviewModalOpen(true);
                    }

                    if (data.initialData?.structuredMenu) {
                        setStructuredMenu(data.initialData.structuredMenu);
                        setRecipes([]);
                        setBrowseRecipes([]);
                    } else {
                        setRecipes(data.initialData?.recipes || []);
                        setStructuredMenu(null);
                    }

                    setAvailableComponents(data.initialData?.availableComponents || []);
                }
            } catch (error) {
                console.error('Initialization error:', error);
                setHasPremium(false);
            } finally {
                setBooting(false);
                setLoading(false);
            }
        };
        initialize();
    }, []);

    const handleFamilyUpdate = async (familyData) => {
        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.post(`${API_BASE_URL}/api/allergen-kitchen/family-update`, familyData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                await handleSearch();
                return { success: true };
            }
            return { success: false };
        } catch (error) {
            console.error('Family update error:', error);
            return { success: false };
        }
    };

    const loadMoreBrowse = async (cats = selectedCategories, allergens = selectedAllergens, includeAdaptive = includeAdaptiveRecipes) => {
        if (loadingMore || !browseHasMore) return;
        const nextPage = browsePage + 1;
        setLoadingMore(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/api/allergen-kitchen/browse`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    preferredCategories: cats, selectedAllergens: allergens,
                    includeAdaptiveRecipes: includeAdaptive, portion: parseInt(portion),
                    page: nextPage, limit: BROWSE_PAGE_SIZE
                })
            });
            const data = await response.json();
            if (data.success) {
                setBrowseRecipes((prev) => [...prev, ...(data.recipes || [])]);
                setBrowsePage(nextPage);
                setBrowseHasMore(!!data.hasMore);
            }
        } catch (err) {
            console.error('Load more error:', err);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleSearch = async (cats = selectedCategories, allergens = selectedAllergens, mode = viewMode, includeAdaptive = includeAdaptiveRecipes) => {
        const requestId = ++searchSeqRef.current;
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            // [Sayfalı browse] "Tüm Tarifler" yalın /browse endpoint'inden 20'şer gelir.
            if (mode === 'all') {
                setBrowsePage(1);
                const response = await fetch(`${API_BASE_URL}/api/allergen-kitchen/browse`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        preferredCategories: cats, selectedAllergens: allergens,
                        includeAdaptiveRecipes: includeAdaptive, portion: parseInt(portion),
                        page: 1, limit: BROWSE_PAGE_SIZE
                    })
                });
                const data = await response.json();
                if (requestId !== searchSeqRef.current) return;
                if (data.success) {
                    setBrowseRecipes(data.recipes || []);
                    setStructuredMenu(null);
                    setRecipes([]);
                    setBrowseHasMore(!!data.hasMore);
                }
                return;
            }
            const response = await fetch(`${API_BASE_URL}/api/allergen-kitchen/suggest`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    preferredCategories: cats,
                    selectedAllergens: allergens,
                    browseAll: mode === 'all',
                    portion: parseInt(portion),
                    includeAdaptiveRecipes: includeAdaptive
                })
            });
            const data = await response.json();
            if (requestId !== searchSeqRef.current) return;
            if (data.success) {
                if (mode === 'all') {
                    setBrowseRecipes(data.recipes || []);
                    setStructuredMenu(null);
                    setRecipes([]);
                    setAvailableComponents(data.availableComponents || []);
                } else if (data.structuredMenu) {
                    setStructuredMenu(data.structuredMenu);
                    setRecipes([]); // Clear old list format
                    setAvailableComponents(data.availableComponents || []);
                } else {
                    setRecipes(data.recipes || []);
                    setStructuredMenu(null);
                    setAvailableComponents(data.availableComponents || []);
                }
            }
        } catch (err) {
            console.error('Search error:', err);
        } finally {
            if (requestId === searchSeqRef.current) {
                setLoading(false);
            }
        }
    };

    const toggleCategory = (catId) => {
        const newCats = selectedCategories.includes(catId)
            ? selectedCategories.filter(c => c !== catId)
            : [...selectedCategories, catId];
        setSelectedCategories(newCats);
        handleSearch(newCats, selectedAllergens, viewMode);
    };

    const toggleAllergen = (allergen) => {
        const isYok = allergen.toLowerCase().includes('yok');
        let newAllergens;

        if (isYok) {
            newAllergens = selectedAllergens.includes(allergen) ? [] : [allergen];
        } else {
            const filtered = selectedAllergens.filter(a => !a.toLowerCase().includes('yok'));
            newAllergens = filtered.includes(allergen)
                ? filtered.filter(a => a !== allergen)
                : [...filtered, allergen];
        }

        const nextIncludeAdaptive = newAllergens.some((item) => !String(item || '').toLowerCase().includes('yok'))
            ? includeAdaptiveRecipes
            : false;

        setSelectedAllergens(newAllergens);
        setIncludeAdaptiveRecipes(nextIncludeAdaptive);
        if (!nextIncludeAdaptive) setShowOnlyAdaptiveRecipes(false);
        handleSearch(selectedCategories, newAllergens, viewMode, nextIncludeAdaptive);
    };

    const toggleSelection = (recipeOrRecipes) => {
        const recipesToToggle = Array.isArray(recipeOrRecipes) ? recipeOrRecipes : [recipeOrRecipes];
        const namesToToggle = recipesToToggle.map(r => r.name || r.title);

        let newSelection = [...selectedRecipeNames];
        const allSelected = namesToToggle.every(name => newSelection.includes(name));

        if (allSelected) {
            newSelection = newSelection.filter(name => !namesToToggle.includes(name));
        } else {
            namesToToggle.forEach(name => {
                if (!newSelection.includes(name)) newSelection.push(name);
            });
        }
        setSelectedRecipeNames(newSelection);
    };

    const handleViewModeToggle = (mode) => {
        setViewMode(mode);
        handleSearch(selectedCategories, selectedAllergens, mode);
    };

    useEffect(() => {
        const hasBrowseResults = browseRecipes.length > 0;
        if (loading || viewMode !== 'all' || !hasActiveAllergenFilter || !hasBrowseResults || includeAdaptiveRecipes) {
            setShowAdaptivePrompt(false);
            setPulseAdaptivePrompt(false);
            return;
        }
        setShowAdaptivePrompt(true);
        setPulseAdaptivePrompt(true);
        const pulseTimer = setTimeout(() => {
            setPulseAdaptivePrompt(false);
        }, 5000);
        return () => clearTimeout(pulseTimer);
    }, [loading, viewMode, hasActiveAllergenFilter, browseRecipes.length, includeAdaptiveRecipes]);

    useEffect(() => {
        if (booting || !hasPremium) return;
        const timeout = setTimeout(() => {
            handleSearch(selectedCategories, selectedAllergens, viewMode);
        }, 350);

        return () => clearTimeout(timeout);
    }, [portion]);

    const handleRecipeClick = async (recipe) => {
        setSelectedRecipe(await hydrateRecipeForModal(recipe));
        setRecipeModalOpen(true);
    };

    const handleMarkMenuAsUsed = async () => {
        let recipesToProcess = [];
        if (viewMode === 'all') {
            recipesToProcess = browseRecipes.filter(r => selectedRecipeNames.includes(r.name || r.title));
        } else if (structuredMenu) {
            // Get all recipes from the structured menu if they are selected
            const menuItems = [
                structuredMenu.main,
                ...structuredMenu.pair,
                structuredMenu.soup,
                structuredMenu.salad
            ].filter(Boolean);
            recipesToProcess = menuItems.filter(r => selectedRecipeNames.includes(r.name || r.title));
        } else {
            recipesToProcess = recipes.filter(r => selectedRecipeNames.includes(r.name || r.title));
        }

        if (recipesToProcess.length === 0) {
            Swal.fire({
                title: 'Tarif Seçmediniz',
                text: 'Lütfen pişirmek istediğiniz tariflerin üzerine tıklayarak seçin.',
                icon: 'info',
                confirmButtonText: 'Anladım'
            });
            return;
        }
        approveMenu(recipesToProcess);
    };

    const handleUpgrade = async () => {
        try {
            const token = localStorage.getItem('authToken');
            await axios.post(`${API_BASE_URL}/api/admin/user-upgrade-allergen`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            window.location.reload();
        } catch (error) {
            console.error('Upgrade failed:', error);
        }
    };

    const handleAdaptiveToggle = async () => {
        if (includeAdaptiveRecipes) {
            setIncludeAdaptiveRecipes(false);
            setShowOnlyAdaptiveRecipes(false);
            setShowAdaptivePrompt(false);
            setPulseAdaptivePrompt(false);
            handleSearch(selectedCategories, selectedAllergens, viewMode, false);
            return;
        }

        const result = await Swal.fire({
            title: 'Alternatifli tarifler açılsın mı?',
            html: `
                <div style="text-align:left;line-height:1.6">
                    <p style="margin-bottom:10px;"><strong>Bu modda</strong> sistem, seçili alerjene sahip bileşenler için tanımlı güvenli alternatifleri kullanabilen tarifleri de göstermeye çalışır.</p>
                    <p style="margin-bottom:10px;color:#b91c1c;"><strong>Önemli:</strong> Çapraz bulaşma riski uyarısı ile açılır.</p>
                    <p style="margin:0;">İleri hassasiyette sadece kesin güvenli tariflerde kalman daha doğru olur.</p>
                </div>
            `,
            icon: 'warning',
            confirmButtonText: 'Anladım, göster',
            cancelButtonText: 'Sadece kesin güvenli kalsın',
            showCancelButton: true,
            reverseButtons: true
        });

        if (!result.isConfirmed) return;

        setIncludeAdaptiveRecipes(true);
        setShowAdaptivePrompt(true);
        setPulseAdaptivePrompt(false);
        handleSearch(selectedCategories, selectedAllergens, viewMode, true);
    };

    if (booting) {
        return (
            <div className="flex h-screen items-center justify-center bg-white dark:bg-[#1a1a1a]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
            </div>
        );
    }

    if (!hasPremium) {
        return (
            <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
                <div className="max-w-4xl mx-auto py-20 px-6">
                    <PremiumLockComponent
                        title="Alerjen Mutfağı"
                        message="Alerjen güvenliğinizi en üst düzeye çıkarın. Akıllı alerjen tarama ve güvenli mutfak asistanını keşfedin."
                        paymentModuleKey="allergen"
                        paymentModuleTitle="Alerjen Mutfağı"
                    />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
            <div className="flex min-h-full flex-col bg-white transition-colors duration-200 dark:bg-[#1a1a1a]">
                {/* Header Section */}
                <div className="border-b border-slate-100 bg-white dark:border-slate-800 dark:bg-[#1a1a1a] px-6 py-6">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-3xl font-black bg-gradient-to-r from-blue-500 to-indigo-600 bg-clip-text text-transparent">Güvenli Mutfak</h1>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium italic">Alerjene uygun ve SKT öncelikli akıllı menü önerileri.</p>
                            </div>
                            <div />
                        </div>
                    </div>
                </div>

                <div className="sticky top-20 z-40 mt-8 px-6 md:top-24">
                    <div className="mx-auto max-w-7xl rounded-[2rem] border border-slate-200 bg-white shadow-none dark:border-slate-800 dark:bg-[#151821]">
                        {/* Responsive Filter Toolbar */}
                        <div className="flex flex-col-reverse gap-6 rounded-t-[2rem] border-b border-slate-200 bg-white p-5 lg:flex-row lg:items-end md:p-6 dark:border-slate-700 dark:bg-[#151821]">

                            {/* Categories */}
                            <div className="flex-1">
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-1 mb-3">MENÜDE NELER OLSUN?</p>
                                <div className="flex flex-wrap gap-2">
                                    {categories.map(cat => {
                                        const isActive = selectedCategories.includes(cat.id);
                                        const activeColors = {
                                            soup: 'bg-amber-500 text-white border-amber-600 shadow-amber-500/20',
                                            main: 'bg-blue-500 text-white border-blue-600 shadow-blue-500/20',
                                            salad: 'bg-emerald-500 text-white border-emerald-600 shadow-emerald-500/20',
                                            dessert: 'bg-purple-500 text-white border-purple-600 shadow-purple-500/20'
                                        };

                                        return (
                                            <button
                                                key={cat.id}
                                                onClick={() => toggleCategory(cat.id)}
                                                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border-2 transition-all active:scale-95 ${isActive
                                                    ? `${activeColors[cat.id]} shadow-md`
                                                    : 'bg-slate-100 border-slate-200 text-slate-700 hover:border-slate-300 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-300 dark:hover:border-slate-600'
                                                    }`}
                                            >
                                                <span className="material-symbols-outlined text-[20px]">{cat.icon}</span>
                                                <span className="text-xs font-bold">{cat.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="hidden lg:block w-px h-12 bg-slate-700 self-center opacity-70" />

                            {/* Preferences */}
                            <div className="flex flex-wrap items-center gap-4">
                                <div className="flex flex-col gap-2">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-1">PORSİYON</p>
                                    <div className="relative h-12 w-24">
                                        <input
                                            type="number"
                                            min={1}
                                            value={portion}
                                            onChange={e => {
                                                setPortion(e.target.value);
                                            }}
                                            className="w-full h-full bg-slate-100 border border-slate-200 rounded-2xl px-4 text-sm font-black text-slate-900 focus:border-blue-500 transition-all outline-none shadow-sm dark:bg-slate-900 dark:border-slate-800 dark:text-white"
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-1">TECRÜBE</p>
                                    <div className="flex bg-slate-100 p-1 rounded-2xl border border-slate-200 shadow-sm h-12 dark:bg-slate-900 dark:border-slate-800">
                                        {['Acemi', 'Usta Şef'].map((lvl) => (
                                            <button
                                                key={lvl}
                                                onClick={() => {
                                                    setSkillLevel(lvl);
                                                    localStorage.setItem('smartSearch_skillLevel', lvl);
                                                }}
                                                className={`px-4 h-full rounded-xl text-[10px] font-black transition-all ${skillLevel === lvl
                                                    ? 'bg-blue-500 text-white shadow-md'
                                                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                                    }`}
                                            >
                                                {lvl.includes('Usta') ? 'Usta' : lvl}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Allergen Filters */}
                        <div className="flex flex-col gap-4 px-5 pb-5 pt-4 md:flex-row md:items-center md:px-6 md:pb-6">
                            <div className="flex items-center gap-2 shrink-0">
                                <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                    <span className="material-symbols-outlined text-red-500 text-[18px]">verified</span>
                                </div>
                                <span className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-tight">ALERJEN KONTROLÜ</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {commonAllergens.slice(0, 6).map(allergen => {
                                    const isActive = selectedAllergens.includes(allergen);
                                    return (
                                        <button
                                            key={allergen}
                                            onClick={() => toggleAllergen(allergen)}
                                            className={`px-3 py-1.5 rounded-xl border text-[10px] font-black transition-all ${isActive
                                                ? 'bg-red-500 border-red-500 text-white shadow-md'
                                                : 'bg-slate-100 border-slate-200 text-slate-700 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-300'
                                                }`}
                                        >
                                            {allergen}
                                        </button>
                                    );
                                })}
                            </div>
                            {(showAdaptivePrompt || includeAdaptiveRecipes) && hasActiveAllergenFilter && viewMode === 'all' && (
                                <div className="md:ml-auto flex flex-col items-start md:items-end gap-1">
                                    <button
                                        onClick={handleAdaptiveToggle}
                                        className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-[11px] font-black transition-all ${includeAdaptiveRecipes
                                            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 shadow-md shadow-emerald-500/10'
                                            : `border-amber-400/40 bg-amber-500/10 text-amber-600 shadow-md shadow-amber-500/10 ${pulseAdaptivePrompt ? 'animate-pulse' : ''}`
                                            }`}
                                    >
                                        <span className="material-symbols-outlined text-[16px]">shield</span>
                                        {includeAdaptiveRecipes ? 'Alternatifli Uyum Açık' : 'Alternatifli Tarifleri de Göster'}
                                    </button>
                                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500">
                                        Çapraz bulaşma riski uyarısı ile açılır.
                                    </p>
                                    {includeAdaptiveRecipes && (
                                        <button
                                            onClick={() => setShowOnlyAdaptiveRecipes(prev => !prev)}
                                            className={`inline-flex items-center gap-2 rounded-2xl border px-3 py-1.5 text-[10px] font-black transition-all ${showOnlyAdaptiveRecipes
                                                ? 'border-indigo-500/40 bg-indigo-500/10 text-indigo-600 shadow-md shadow-indigo-500/10'
                                                : 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
                                                }`}
                                        >
                                            <span className="material-symbols-outlined text-[14px]">tune</span>
                                            {showOnlyAdaptiveRecipes ? 'Sadece Uyarlanmışlar Açık' : 'Sadece Uyarlanmışları Göster'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex-1 p-6 pb-32 pt-8 md:pt-10">
                    <div className="max-w-7xl mx-auto">
                        {loading && (
                            <div className="mb-6 flex items-center gap-3 rounded-3xl border border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40 px-5 py-4">
                                <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500"></div>
                                <p className="text-sm font-bold text-slate-500 dark:text-slate-300">Alerjen dostu menü hazırlanıyor...</p>
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 min-h-[400px]">
                            {(() => {
                                if (viewMode === 'matching' && structuredMenu) {
                                    const { main, pair, soup, salad, dessert } = structuredMenu;
                                    const orderedItems = [
                                        { id: 'soup', data: soup, label: "ÇORBA" },
                                        { id: 'main', data: main, label: "ANA YEMEK", pairings: pair },
                                        { id: 'salad', data: salad, label: "SALATA" },
                                        { id: 'dessert', data: dessert, label: "TATLI" }
                                    ];

                                    return (
                                        <React.Fragment>
                                            {orderedItems.map((item) => {
                                                if (!selectedCategories.includes(item.id) || !item.data) return null;

                                                const recipeWithPairs = item.id === 'main'
                                                    ? { ...item.data, suggestedPairings: item.pairings }
                                                    : item.data;

                                                return (
                                                    <div key={item.id} className={`${item.id === 'main' && item.pairings?.length > 0 ? 'md:col-span-2 lg:col-span-2 xl:col-span-2' : ''} flex flex-col h-full`}>
                                                        <div className="mb-3 px-2 flex-none">
                                                            <span className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-black rounded-lg uppercase tracking-widest border border-slate-200 dark:border-slate-700">
                                                                {item.label}
                                                            </span>
                                                        </div>
                                                        <div className="flex-1">
                                                            <RecipeMenuGroup
                                                                recipe={recipeWithPairs}
                                                                isDarkMode={isDarkMode}
                                                                onCardClick={handleRecipeClick}
                                                                onToggleSelect={toggleSelection}
                                                                selectedRecipeNames={selectedRecipeNames}
                                                                isModern={true}
                                                                userPortion={parseInt(portion)}
                                                                highlightAdaptive={includeAdaptiveRecipes}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                }

                                const dataSource = viewMode === 'all' ? browseRecipes : recipes;
                                if (dataSource.length === 0) {
                                    return (
                                        <div className="col-span-full py-24 text-center">
                                            <div className="size-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-6">
                                                <span className="material-symbols-outlined text-[40px] text-slate-400">restaurant_menu</span>
                                            </div>
                                            <h3 className="text-xl font-bold text-slate-800 dark:text-white">Henüz uygun tarif yok</h3>
                                            <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-sm mx-auto">Seçtiğiniz kriterlerde ve elinizdeki malzemelerle yapılabilecek bir tarif bulamadık.</p>
                                        </div>
                                    );
                                }

                                let finalizedList = [];
                                if (viewMode === 'matching') {
                                    // Fallback for flat list if structuredMenu is missing but we are in matching mode
                                    const processedCats = new Set();
                                    const sortedByScore = [...dataSource].sort((a, b) => b.score - a.score);

                                    sortedByScore.forEach(r => {
                                        const rawIdx = r.categoryIndex !== undefined ? r.categoryIndex : 1;
                                        const cat = Number(rawIdx);
                                        const finalCat = isNaN(cat) ? 1 : cat;

                                        if (!processedCats.has(finalCat)) {
                                            finalizedList.push(r);
                                            processedCats.add(finalCat);
                                        }
                                    });
                                } else {
                                    finalizedList = dataSource;
                                }

                                if (viewMode === 'all' && includeAdaptiveRecipes && showOnlyAdaptiveRecipes) {
                                    finalizedList = finalizedList.filter((item) => isRecipeAdapted(item));
                                }

                                if (finalizedList.length === 0) {
                                    return (
                                        <div className="col-span-full py-24 text-center">
                                            <div className="size-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-6">
                                                <span className="material-symbols-outlined text-[40px] text-slate-400">filter_alt</span>
                                            </div>
                                            <h3 className="text-xl font-bold text-slate-800 dark:text-white">Uyarlanmış tarif bulunamadı</h3>
                                            <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-sm mx-auto">Bu filtre açıkken yalnızca güvenli alternatifle uyarlanabilen tarifleri gösteriyoruz. İstersen filtreyi kapatıp tüm güvenli sonuçları yeniden görebilirsin.</p>
                                        </div>
                                    );
                                }

                                // Sıralama: "Tüm Tarifler"de malzeme/stok eşleşmesi yüksek
                                // tarifler üstte (yapılabilirlik); eşitlikte menü sırası.
                                // Matching görünümünde yalnız menü sırası.
                                const menuOrder = { 0: 1, 1: 2, 5: 3, 2: 4, 3: 5, 4: 6, 99: 7 };
                                const menuRank = (r) => menuOrder[r.categoryIndex !== undefined ? r.categoryIndex : 99] || 99;
                                const matchOf = (r) => Number(r.matchPercentage ?? r.facts?.matchPercentage ?? r.presentation?.facts?.matchPercentage ?? 0);
                                if (viewMode === 'all') {
                                    finalizedList.sort((a, b) => {
                                        const diff = matchOf(b) - matchOf(a);
                                        if (diff !== 0) return diff;
                                        return menuRank(a) - menuRank(b);
                                    });
                                } else {
                                    finalizedList.sort((a, b) => menuRank(a) - menuRank(b));
                                }

                                return finalizedList.map((recipe, idx) => (
                                    <div key={idx} className={`${recipe.suggestedPairings?.length > 0 ? 'md:col-span-2 lg:col-span-2 xl:col-span-2' : ''} flex flex-col h-full`}>
                                        <RecipeMenuGroup
                                            recipe={recipe}
                                            isDarkMode={isDarkMode}
                                            onCardClick={handleRecipeClick}
                                            onToggleSelect={toggleSelection}
                                            selectedRecipeNames={selectedRecipeNames}
                                            isModern={true}
                                            userPortion={parseInt(portion)}
                                            highlightAdaptive={includeAdaptiveRecipes}
                                            className="flex-1"
                                        />
                                    </div>
                                ));
                            })()}
                        </div>
                        {viewMode === 'all' && browseHasMore && !loading && (
                            <div className="mt-8 flex justify-center">
                                <button
                                    onClick={() => loadMoreBrowse()}
                                    disabled={loadingMore}
                                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-[#151821] dark:text-slate-200"
                                >
                                    {loadingMore ? (
                                        <><span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-rose-500"></span> Yükleniyor…</>
                                    ) : (
                                        <><span className="material-symbols-outlined text-[18px]">expand_more</span> Daha fazla tarif</>
                                    )}
                                </button>
                            </div>
                        )}
                        <div className="mt-12 flex justify-center">
                            <div className="inline-flex items-center rounded-2xl border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-[#151821]">
                                <button
                                    onClick={() => handleViewModeToggle(viewMode === 'matching' ? 'all' : 'matching')}
                                    className="inline-flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-rose-600 transition"
                                >
                                    <span className="material-symbols-outlined text-[16px]">menu_book</span>
                                    {viewMode === 'matching' ? 'Tüm Tarifler' : 'Bugünün Menüsüne Dön'}
                                </button>
                                <button
                                    onClick={() => handleViewModeToggle(viewMode === 'matching' ? 'all' : 'matching')}
                                    className="px-5 py-2.5 text-xs font-black uppercase tracking-wider text-slate-600 hover:text-slate-900 transition dark:text-slate-300 dark:hover:text-white"
                                >
                                    {viewMode === 'matching' ? 'Daha Fazla Keşfet' : 'Kişiye Özel Menü'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Utility Bar */}
                {selectedRecipeNames.length > 0 && (
                    <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] animate-in slide-in-from-bottom-10 duration-500">
                        <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-slate-200 dark:border-slate-800 rounded-[2.5rem] p-2 pl-8 shadow-2xl flex items-center gap-8 ring-4 ring-slate-100/50 dark:ring-slate-800/20">
                            <div className="flex flex-col">
                                <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] leading-none mb-1">GÜVENLİ MENÜ</p>
                                <p className="text-sm font-black text-slate-800 dark:text-white leading-none">{selectedRecipeNames.length} Tarif Seçildi</p>
                            </div>
                            <button
                                onClick={handleMarkMenuAsUsed}
                                disabled={isApproving}
                                className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-[2rem] text-white font-black text-xs hover:shadow-lg hover:shadow-green-500/30 transition-all active:scale-95 disabled:grayscale"
                            >
                                {isApproving ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                ) : (
                                    <span className="material-symbols-outlined text-[18px]">skillet</span>
                                )}
                                ONAYLA VE PİŞİR</button>
                        </div>
                    </div>
                )}
            </div>

            {recipeModalOpen && selectedRecipe && (
                <RecipeModal
                    onPairingClick={handleRecipeClick}
                    isOpen={recipeModalOpen}
                    onClose={() => setRecipeModalOpen(false)}
                    recipe={selectedRecipe}
                    isDarkMode={isDarkMode}
                    userProfile={userProfile}
                    userPortion={parseInt(portion)}
                    skillLevel={skillLevel}
                    availableComponents={availableComponents}
                />
            )}

            {
                reviewModalOpen && (
                    <AllergyReviewModal
                        isOpen={reviewModalOpen}
                        onClose={() => setReviewModalOpen(false)}
                        onSave={handleFamilyUpdate}
                        isDarkMode={isDarkMode}
                    />
                )
            }
        </DashboardLayout >
    );
};

export default AllergenKitchen;
